/**
 * One card's cell on the dashboard unit grid.
 *
 * A card declares how many units wide it is and nothing else — the grid decides
 * how wide a unit currently is. A card asking for more units than the stage can
 * hold falls back to a single unit rather than overflowing, so the same
 * declaration serves a phone and a widescreen monitor. `full` is the exception:
 * it takes the whole row whatever the column count.
 *
 * Contains:
 * - DashboardCard — the grid cell component (default export)
 */
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import React from 'react';

import { MAX_UNITS, spanUnitsSx } from '@sophocles/views/sites/components/SiteDashboard/dashboardUnits';

const FULL_ROW_SX = { gridColumn: '1 / -1' };

/**
 * @param {object}     props
 * @param {number|'full'} [props.span=1] - width in units, or `full` for the whole row
 * @param {object}     [props.sx]        - optional MUI sx overrides for this grid cell
 * @param {React.node} props.children
 */
export default function DashboardCard({ span = 1, sx, children, ...boxProps }) {
  const spanSx = span === 'full' ? FULL_ROW_SX : spanUnitsSx(span);
  const sxList = Array.isArray(sx) ? sx : [sx].filter(Boolean);

  return (
    <Box
      {...boxProps}
      sx={[
        {
          ...spanSx,
          minWidth: 0,
          display: 'flex',
          '& > *': { flex: 1, minWidth: 0 },
        },
        ...sxList,
      ]}
    >
      {children}
    </Box>
  );
}

DashboardCard.propTypes = {
  span: PropTypes.oneOfType([PropTypes.oneOf([...Array(MAX_UNITS).keys()].map(i => i + 1)), PropTypes.oneOf(['full'])]),
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
};
